/**
 * Executive Intelligence Briefing (Iter 76.17)
 * --------------------------------------------
 * Presentation-layer redesign of the "Morning Executive Digest" into an
 * enterprise-grade Executive Intelligence Briefing.
 *
 * IMPORTANT: This component is PRESENTATION ONLY. It consumes existing
 * endpoints — no AI generation logic, scoring algorithms, calculations,
 * business logic, APIs, database schemas, permissions, routing, or
 * analytics are modified.
 *
 * Data sources (all pre-existing):
 *   - /api/executive-ops/daily-digest/latest  (narrative + citations + copilot_used + posted_at)
 *   - overview.executive_summary              (org health, risk rating, controls)
 *   - overview.framework_readiness            (per-framework percentages)
 *   - overview.risk_dashboard                 (critical/high/medium counts)
 *   - overview.remediation_dashboard          (SLA + open actions)
 *   - /api/executive-ops/timeline             (executive activity events)
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Sunrise, ShieldCheck, AlertTriangle, Activity, ClipboardList,
  TrendingUp, TrendingDown, Minus, ChevronDown, ChevronRight,
  Printer, Download, FileText, Sparkles, Info, ArrowRight,
  CheckCircle2, XCircle, Circle, ExternalLink, Zap,
  UserCheck, Clock, Layers, LineChart as LineChartIcon,
} from "lucide-react";
import MetricHelp from "@/components/MetricHelp";
import NotifyLeadershipButton from "@/components/NotifyLeadershipButton";

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────
const FRAMEWORK_LABEL = {
  hipaa: "HIPAA",
  soc2: "SOC 2",
  nist_csf: "NIST CSF",
  cfr42_part2: "42 CFR Part 2",
};

function gradeFromScore(s) {
  if (s == null) return "—";
  if (s >= 95) return "A+";
  if (s >= 90) return "A";
  if (s >= 85) return "B+";
  if (s >= 80) return "B";
  if (s >= 70) return "C";
  if (s >= 60) return "D";
  return "F";
}

function toneFromScore(s) {
  if (s == null) return { bg: "#F1F5F9", fg: "#475569", accent: "#94A3B8" };
  if (s >= 90) return { bg: "#DCFCE7", fg: "#166534", accent: "#22C55E" };
  if (s >= 80) return { bg: "#DBEAFE", fg: "#1E3A8A", accent: "#3B82F6" };
  if (s >= 70) return { bg: "#FEF3C7", fg: "#92400E", accent: "#F59E0B" };
  return { bg: "#FEE2E2", fg: "#991B1B", accent: "#EF4444" };
}

function trendIcon(deltaOrTrend) {
  if (deltaOrTrend == null) return { Icon: Minus, tone: "text-slate-400", label: "—" };
  if (typeof deltaOrTrend === "string") {
    if (deltaOrTrend === "up" || deltaOrTrend === "improving")
      return { Icon: TrendingUp, tone: "text-[#166534]", label: "Improving" };
    if (deltaOrTrend === "down" || deltaOrTrend === "declining")
      return { Icon: TrendingDown, tone: "text-[#991B1B]", label: "Declining" };
    return { Icon: Minus, tone: "text-slate-400", label: "Flat" };
  }
  if (deltaOrTrend > 0.5) return { Icon: TrendingUp, tone: "text-[#166534]", label: `+${deltaOrTrend.toFixed(1)}` };
  if (deltaOrTrend < -0.5) return { Icon: TrendingDown, tone: "text-[#991B1B]", label: `${deltaOrTrend.toFixed(1)}` };
  return { Icon: Minus, tone: "text-slate-400", label: "Flat" };
}

/**
 * Parse the LLM-generated narrative into structured executive content
 * WITHOUT modifying the AI itself. Extracts:
 *   - summaryBullets: up to 5 short takeaways (prose sentences or explicit bullets)
 *   - recommendations: bullet-like lines (starting with "-", "•", or numbered)
 *   - The "Sources:" line is intentionally ignored here — already in `citations`.
 */
function parseNarrative(narrative) {
  if (!narrative) return { summaryBullets: [], recommendations: [] };
  const lines = narrative.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const bullets = [];
  const prose = [];
  for (const line of lines) {
    if (/^sources?:/i.test(line)) continue;
    if (/^(\s*)([-•*]|\d+[.)])\s+/.test(line)) {
      bullets.push(line.replace(/^(\s*)([-•*]|\d+[.)])\s+/, "").trim());
    } else {
      prose.push(line);
    }
  }
  // Executive summary: first 4-5 substantive sentences, capped at 180 chars each.
  const sentences = prose.join(" ")
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 15 && s.length < 260)
    .slice(0, 5);
  const summaryBullets = sentences.length ? sentences : bullets.slice(0, 5);
  return {
    summaryBullets,
    recommendations: bullets,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Feature 1 — Executive Snapshot (4 KPI cards)
// ─────────────────────────────────────────────────────────────────────────────
function SnapshotCard({ testId, eyebrow, EyebrowIcon, value, valueSub, hint,
                        trend, helpTopic, tone, footer, footerLink, footerLinkLabel }) {
  const t = tone || toneFromScore(null);
  const tr = trend ? trendIcon(trend) : null;
  return (
    <div
      className="eib__snapshot-card"
      data-testid={testId}
      style={{ borderTopColor: t.accent }}
    >
      <div className="eib__snapshot-eyebrow">
        {EyebrowIcon && <EyebrowIcon className="w-3.5 h-3.5" />}
        <span>{eyebrow}</span>
        {helpTopic && <MetricHelp topicKey={helpTopic} size="sm" side="bottom" align="start" />}
        {tr && (
          <span className={`eib__snapshot-trend ${tr.tone}`}>
            <tr.Icon className="w-3.5 h-3.5" />
            <span>{tr.label}</span>
          </span>
        )}
      </div>
      <div className="eib__snapshot-value" style={{ color: t.fg }}>
        {value}
        {valueSub && <span className="eib__snapshot-value-sub">{valueSub}</span>}
      </div>
      {hint && <div className="eib__snapshot-hint">{hint}</div>}
      {(footer || footerLink) && (
        <div className="eib__snapshot-footer">
          {footerLink ? (
            <Link to={footerLink} className="eib__snapshot-footer-link"
                  data-testid={`${testId}-drilldown`}>
              {footerLinkLabel || "Open"} <ArrowRight className="w-3 h-3" />
            </Link>
          ) : footer}
        </div>
      )}
    </div>
  );
}

function ExecutiveSnapshot({ overview, digestPostedAt }) {
  const es = overview?.executive_summary || {};
  const orgScore = es.overall_compliance_score ?? 0;
  const orgTone = toneFromScore(orgScore);

  // Risk dashboard: API returns `totals.{critical,high,medium}` — NOT `by_severity`.
  const riskDash = overview?.risk_dashboard || {};
  const totals = riskDash?.totals || {};
  const critical = totals.critical ?? riskDash?.by_severity?.critical ?? 0;
  const high = totals.high ?? 0;
  const medium = totals.medium ?? 0;
  const newSinceYesterday = riskDash?.new_since_yesterday
    ?? totals.new_since_yesterday ?? 0;

  // Remediation dashboard: API returns `open_remediation`, `overdue_remediation`,
  // `assigned_owners`. NOT `total_open`, `due_today`.
  const remediation = overview?.remediation_dashboard || {};
  const openActions = remediation?.open_remediation ?? remediation?.total_open ?? 0;
  const overdue = remediation?.overdue_remediation ?? remediation?.due_today ?? 0;
  const owners = remediation?.assigned_owners
    ?? Object.keys(remediation?.by_owner_open || {}).length;

  const frameworks = overview?.framework_readiness || [];

  return (
    <section className="eib__section eib__snapshot" data-testid="eib-snapshot">
      <div className="eib__section-head">
        <h3><Activity className="w-4 h-4" /> Executive Snapshot</h3>
        <span className="eib__section-sub">
          Understand organizational health at a glance
          {digestPostedAt && ` · updated ${new Date(digestPostedAt).toLocaleString()}`}
        </span>
      </div>

      <div className="eib__snapshot-grid">
        {/* 1. Overall Organizational Health */}
        <SnapshotCard
          testId="eib-snapshot-health"
          EyebrowIcon={Activity}
          eyebrow="Organizational Health"
          helpTopic="organization_health"
          tone={orgTone}
          value={<>{orgScore.toFixed(1)}<span className="eib__snapshot-value-sub">/100</span></>}
          hint={<>Grade <strong>{gradeFromScore(orgScore)}</strong> · Risk rating <strong>{es.organizational_risk_rating_label || "—"}</strong></>}
          footer={<span className="eib__snapshot-footer-note">Priority: {orgScore >= 90 ? "Maintain" : orgScore >= 80 ? "Monitor" : orgScore >= 70 ? "Improve" : "Urgent"}</span>}
          footerLink="/admin/compliance"
          footerLinkLabel="Open Compliance Center"
        />

        {/* 2. Compliance Status */}
        <div className="eib__snapshot-card eib__snapshot-card--compliance"
             data-testid="eib-snapshot-compliance"
             style={{ borderTopColor: orgTone.accent }}>
          <div className="eib__snapshot-eyebrow">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Compliance Status</span>
            <MetricHelp topicKey="compliance_snapshot" size="sm" side="bottom" align="start" />
          </div>
          <div className="eib__snapshot-compliance-grid">
            {frameworks.slice(0, 4).map((fw) => {
              const pctRaw = fw.readiness_pct ?? fw.readiness_score ?? 0;
              const pct = typeof pctRaw === "number" ? pctRaw : Number(pctRaw) || 0;
              const fwt = toneFromScore(pct);
              return (
                <Link key={fw.framework_id}
                      to={`/admin/regulatory?framework=${fw.framework_id}`}
                      className="eib__snapshot-compliance-row"
                      data-testid={`eib-fw-${fw.framework_id}`}>
                  <span className="eib__snapshot-compliance-label">
                    {FRAMEWORK_LABEL[fw.framework_id] || fw.framework_id}
                  </span>
                  <span className="eib__snapshot-compliance-pct" style={{ color: fwt.fg }}>
                    {pct.toFixed(0)}%
                  </span>
                </Link>
              );
            })}
            {!frameworks.length && <span className="eib__snapshot-hint">No framework data</span>}
          </div>
        </div>

        {/* 3. Critical Risks */}
        <SnapshotCard
          testId="eib-snapshot-risks"
          EyebrowIcon={AlertTriangle}
          eyebrow="Critical Risks"
          helpTopic="active_alerts"
          tone={toneFromScore(critical === 0 ? 100 : critical <= 2 ? 75 : 40)}
          value={critical}
          valueSub={<span className="eib__snapshot-value-sub">critical</span>}
          hint={<>{high} high · {medium} medium · {newSinceYesterday} new since yesterday</>}
          footerLink="/admin/exceptions"
          footerLinkLabel="Open Risk Register"
        />

        {/* 4. Executive Action Queue */}
        <SnapshotCard
          testId="eib-snapshot-actions"
          EyebrowIcon={ClipboardList}
          eyebrow="Action Queue"
          tone={toneFromScore(openActions === 0 ? 100 : openActions <= 5 ? 80 : 50)}
          value={openActions}
          valueSub={<span className="eib__snapshot-value-sub">open</span>}
          hint={<>{overdue} overdue · {owners} owner{owners === 1 ? "" : "s"}</>}
          footerLink="/admin/exceptions"
          footerLinkLabel="Open Action Center"
        />
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Feature 2 — Executive Summary (bullet points from narrative)
// ─────────────────────────────────────────────────────────────────────────────
function ExecutiveSummary({ bullets, hasCopilot }) {
  if (!bullets.length) return null;
  return (
    <section className="eib__section" data-testid="eib-summary">
      <div className="eib__section-head">
        <h3><FileText className="w-4 h-4" /> Executive Summary</h3>
        <span className="eib__section-sub">
          {hasCopilot ? "Powered by HarborWell AI Copilot" : "Data-driven summary"}
        </span>
      </div>
      <ul className="eib__bullet-list" data-testid="eib-summary-bullets">
        {bullets.slice(0, 5).map((b, i) => (
          <li key={i} className="eib__bullet-item">
            <Circle className="w-2 h-2 mt-1.5 shrink-0 fill-[#1E3A8A] text-[#1E3A8A]" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Feature 3 — Critical Risk Center
// ─────────────────────────────────────────────────────────────────────────────
const SEVERITY_TONE = {
  critical: { bg: "#FEE2E2", fg: "#991B1B", ring: "#EF4444", label: "Critical" },
  high:     { bg: "#FEF3C7", fg: "#92400E", ring: "#F59E0B", label: "High" },
  medium:   { bg: "#DBEAFE", fg: "#1E40AF", ring: "#3B82F6", label: "Medium" },
  low:      { bg: "#F1F5F9", fg: "#475569", ring: "#94A3B8", label: "Low" },
};

function buildRiskItems(overview) {
  const items = [];
  const rd = overview?.risk_dashboard || {};
  const fw = overview?.framework_readiness || [];

  // Framework findings — turn any framework <90% into a risk card.
  fw.forEach((f) => {
    const pctRaw = f.readiness_pct ?? f.readiness_score ?? 100;
    const pct = typeof pctRaw === "number" ? pctRaw : Number(pctRaw) || 0;
    if (pct < 90) {
      const sev = pct < 70 ? "critical" : pct < 80 ? "high" : "medium";
      items.push({
        id: `fw:${f.framework_id}`,
        title: `${FRAMEWORK_LABEL[f.framework_id] || f.framework_id} readiness at ${pct.toFixed(0)}%`,
        severity: sev,
        impact: sev === "critical" ? "Regulatory exposure & audit findings likely" :
                sev === "high" ? "Elevated audit-finding risk" :
                "Minor drift — monitor closely",
        program: FRAMEWORK_LABEL[f.framework_id] || f.framework_id,
        owner: f.owner || "Compliance Officer",
        recommendedAction: `Review ${FRAMEWORK_LABEL[f.framework_id] || f.framework_id} controls`,
        link: `/admin/regulatory?framework=${f.framework_id}`,
        lastUpdated: overview?.meta?.computed_at,
      });
    }
  });

  // Named risks from risk_dashboard.top_risks (if present).
  (rd.top_risks || []).slice(0, 5).forEach((r, i) => {
    items.push({
      id: `risk:${r.id || i}`,
      title: r.title || r.name || "Elevated risk detected",
      severity: (r.severity || "high").toLowerCase(),
      impact: r.business_impact || r.impact || "Operational or compliance exposure.",
      program: r.program || r.category || "Cross-functional",
      owner: r.owner || r.owner_email || "—",
      recommendedAction: r.recommended_action || r.remediation || "Investigate root cause",
      link: r.link || "/admin/exceptions",
      lastUpdated: r.updated_at || overview?.meta?.computed_at,
    });
  });

  // Priority: critical → high → medium
  const rank = { critical: 0, high: 1, medium: 2, low: 3 };
  items.sort((a, b) => (rank[a.severity] ?? 9) - (rank[b.severity] ?? 9));
  return items.slice(0, 6);
}

function RiskCard({ risk }) {
  const tone = SEVERITY_TONE[risk.severity] || SEVERITY_TONE.medium;
  return (
    <article className="eib__risk-card" data-testid={`eib-risk-${risk.id}`}
             style={{ borderLeftColor: tone.ring }}>
      <header className="eib__risk-head">
        <span className="eib__risk-severity"
              style={{ background: tone.bg, color: tone.fg }}>
          {tone.label}
        </span>
        <span className="eib__risk-title">{risk.title}</span>
      </header>
      <dl className="eib__risk-facts">
        <div><dt>Business Impact</dt><dd>{risk.impact}</dd></div>
        <div><dt>Affected Program</dt><dd>{risk.program}</dd></div>
        <div><dt>Owner</dt><dd>{risk.owner}</dd></div>
        <div><dt>Recommended Action</dt><dd>{risk.recommendedAction}</dd></div>
      </dl>
      <footer className="eib__risk-foot">
        <span className="eib__risk-updated">
          Updated {risk.lastUpdated ? new Date(risk.lastUpdated).toLocaleString() : "—"}
        </span>
        <div className="eib__risk-actions">
          <Link to={risk.link} className="eib__btn eib__btn--outline"
                data-testid={`eib-risk-${risk.id}-view`}>
            View details <ExternalLink className="w-3 h-3" />
          </Link>
          <NotifyLeadershipButton risk={risk} />
          <Link to={`${risk.link}?action=investigate`} className="eib__btn eib__btn--primary"
                data-testid={`eib-risk-${risk.id}-investigate`}>
            Open investigation
          </Link>
        </div>
      </footer>
    </article>
  );
}

function CriticalRiskCenter({ overview }) {
  const risks = useMemo(() => buildRiskItems(overview), [overview]);
  if (!risks.length) {
    return (
      <section className="eib__section" data-testid="eib-risks">
        <div className="eib__section-head">
          <h3><AlertTriangle className="w-4 h-4" /> Critical Risk Center</h3>
        </div>
        <div className="eib__empty" data-testid="eib-risks-empty">
          <CheckCircle2 className="w-5 h-5 text-[#166534]" />
          <div>
            <strong>No critical risks flagged.</strong>
            <p>All frameworks are performing within acceptable thresholds.</p>
          </div>
        </div>
      </section>
    );
  }
  return (
    <section className="eib__section" data-testid="eib-risks">
      <div className="eib__section-head">
        <h3><AlertTriangle className="w-4 h-4" /> Critical Risk Center</h3>
        <span className="eib__section-sub">Top {risks.length} risk{risks.length === 1 ? "" : "s"} — sorted by severity</span>
      </div>
      <div className="eib__risk-grid">
        {risks.map((r) => <RiskCard key={r.id} risk={r} />)}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Feature 4/5/6 — Recommended Actions + Evidence + Explain
// ─────────────────────────────────────────────────────────────────────────────
const ACTION_PRIORITY_TONE = {
  critical: { bg: "#FEE2E2", fg: "#991B1B" },
  high:     { bg: "#FEF3C7", fg: "#92400E" },
  medium:   { bg: "#DBEAFE", fg: "#1E40AF" },
  low:      { bg: "#F1F5F9", fg: "#475569" },
};

function buildRecommendations(overview, aiRecommendations, citations) {
  const items = [];
  const es = overview?.executive_summary || {};
  const rd = overview?.risk_dashboard || {};
  const now = new Date();

  const push = (rec) => items.push({
    dueDate: null, estimatedImpact: "Medium", ...rec,
  });

  // Data-driven recommendations
  if ((es.overall_compliance_score ?? 100) < 90) {
    push({
      id: "rec:compliance",
      priority: (es.overall_compliance_score ?? 100) < 80 ? "critical" : "high",
      recommendation: "Address open compliance findings",
      department: "Compliance & Risk",
      estimatedImpact: `${(90 - (es.overall_compliance_score ?? 0)).toFixed(0)}+ pts to overall score`,
      link: "/admin/compliance",
      linkLabel: "Open Compliance Center",
      whyMetrics: [
        `Overall compliance score: ${(es.overall_compliance_score ?? 0).toFixed(1)}`,
        `Controls requiring attention: ${es.controls_requiring_attention ?? 0}`,
        `High-risk findings: ${es.high_risk_findings ?? 0}`,
      ],
    });
  }

  const rdTotals = rd?.totals || {};
  const criticalCount = rdTotals.critical ?? rd?.by_severity?.critical ?? 0;
  if (criticalCount > 0) {
    push({
      id: "rec:critical-risks",
      priority: "critical",
      recommendation: `Investigate ${criticalCount} critical risk${criticalCount === 1 ? "" : "s"}`,
      department: "Executive Leadership",
      estimatedImpact: "Prevent regulatory exposure",
      dueDate: now.toISOString().slice(0, 10),
      link: "/admin/exceptions",
      linkLabel: "Open Risk Register",
      whyMetrics: [
        `Critical risks: ${criticalCount}`,
        `High: ${rdTotals.high ?? 0} · Medium: ${rdTotals.medium ?? 0}`,
      ],
    });
  }

  (overview?.framework_readiness || []).forEach((fw) => {
    const pctRaw = fw.readiness_pct ?? fw.readiness_score ?? 100;
    const pct = typeof pctRaw === "number" ? pctRaw : Number(pctRaw) || 0;
    if (pct < 85) {
      push({
        id: `rec:fw:${fw.framework_id}`,
        priority: pct < 70 ? "critical" : pct < 80 ? "high" : "medium",
        recommendation: `Improve ${FRAMEWORK_LABEL[fw.framework_id] || fw.framework_id} readiness`,
        department: "Compliance",
        estimatedImpact: `Framework: +${(90 - pct).toFixed(0)} pts`,
        link: `/admin/regulatory?framework=${fw.framework_id}`,
        linkLabel: `Open ${FRAMEWORK_LABEL[fw.framework_id] || fw.framework_id}`,
        whyMetrics: [
          `Current readiness: ${pct.toFixed(1)}%`,
          `Controls: ${fw.total_controls ?? "—"} · gaps: ${fw.gap_count ?? "—"}`,
        ],
      });
    }
  });

  // AI-extracted recommendations from the digest bullets (if any).
  (aiRecommendations || []).slice(0, 5).forEach((b, i) => {
    push({
      id: `rec:ai:${i}`,
      priority: "medium",
      recommendation: b,
      department: "Executive AI Copilot",
      estimatedImpact: "See supporting evidence",
      link: "/admin/operations",
      linkLabel: "Open Ops Center",
      whyMetrics: [
        "Derived from AI Copilot narrative synthesis",
        `Sources: ${(citations || []).join(", ") || "—"}`,
      ],
    });
  });

  // Dedupe by recommendation text; cap 8 items.
  const seen = new Set();
  return items.filter((r) => {
    const key = r.recommendation.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 8);
}

function RecommendationRow({ rec, citations, digestPostedAt }) {
  const [expanded, setExpanded] = useState(false);
  const tone = ACTION_PRIORITY_TONE[rec.priority] || ACTION_PRIORITY_TONE.medium;
  return (
    <>
      <tr className="eib__rec-row" data-testid={`eib-rec-${rec.id}`}>
        <td>
          <span className="eib__rec-priority"
                style={{ background: tone.bg, color: tone.fg }}>
            {rec.priority.toUpperCase()}
          </span>
        </td>
        <td>
          <div className="eib__rec-title">{rec.recommendation}</div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="eib__rec-why"
            data-testid={`eib-rec-${rec.id}-why-toggle`}
            aria-expanded={expanded}
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Why did HarborWell AI recommend this?
          </button>
        </td>
        <td className="eib__rec-dept">{rec.department}</td>
        <td className="eib__rec-due">{rec.dueDate || "—"}</td>
        <td className="eib__rec-impact">{rec.estimatedImpact}</td>
        <td>
          <Link to={rec.link} className="eib__btn eib__btn--primary eib__btn--sm"
                data-testid={`eib-rec-${rec.id}-open`}>
            {rec.linkLabel}
          </Link>
        </td>
      </tr>
      {expanded && (
        <tr className="eib__rec-evidence-row" data-testid={`eib-rec-${rec.id}-evidence`}>
          <td colSpan={6}>
            <div className="eib__rec-evidence">
              <div className="eib__rec-evidence-col">
                <h5><Info className="w-3.5 h-3.5" /> Primary contributing metrics</h5>
                <ul>
                  {(rec.whyMetrics || []).map((m, i) => (
                    <li key={i}><span className="eib__evidence-check">✓</span> {m}</li>
                  ))}
                </ul>
              </div>
              <div className="eib__rec-evidence-col">
                <h5><Sparkles className="w-3.5 h-3.5" /> Evidence sources</h5>
                <ul>
                  {(citations && citations.length ? citations : ["Executive Summary", "Framework Readiness", "Risk Dashboard"])
                    .map((c, i) => (<li key={i}><span className="eib__evidence-check">✓</span> {c}</li>))}
                </ul>
                <div className="eib__rec-evidence-meta">
                  <Clock className="w-3 h-3" />
                  Last refresh {digestPostedAt ? new Date(digestPostedAt).toLocaleString() : "—"}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function RecommendedActions({ recommendations, citations, digestPostedAt }) {
  if (!recommendations.length) {
    return (
      <section className="eib__section" data-testid="eib-actions">
        <div className="eib__section-head">
          <h3><Zap className="w-4 h-4" /> Recommended Actions</h3>
        </div>
        <div className="eib__empty" data-testid="eib-actions-empty">
          <CheckCircle2 className="w-5 h-5 text-[#166534]" />
          <div>
            <strong>No urgent recommendations.</strong>
            <p>Organizational metrics are within healthy thresholds today.</p>
          </div>
        </div>
      </section>
    );
  }
  return (
    <section className="eib__section" data-testid="eib-actions">
      <div className="eib__section-head">
        <h3><Zap className="w-4 h-4" /> Recommended Actions</h3>
        <span className="eib__section-sub">
          Prioritized action queue for today · click <em>Why did HarborWell AI recommend this?</em> to see the evidence
        </span>
      </div>
      <div className="eib__rec-table-wrap">
        <table className="eib__rec-table" data-testid="eib-actions-table">
          <thead>
            <tr>
              <th>Priority</th>
              <th>Recommendation</th>
              <th>Responsible</th>
              <th>Due Date</th>
              <th>Estimated Impact</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {recommendations.map((r) => (
              <RecommendationRow key={r.id} rec={r}
                                 citations={citations}
                                 digestPostedAt={digestPostedAt} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AI Evidence Panel (Feature 5, standalone collapsible summary)
// ─────────────────────────────────────────────────────────────────────────────
function EvidencePanel({ citations, copilotUsed, digestPostedAt, recommendationCount }) {
  const [open, setOpen] = useState(false);
  const sources = citations && citations.length ? citations : [
    "Executive Summary", "Framework Readiness", "Risk Dashboard",
    "Remediation Dashboard", "Compliance Engine",
  ];
  const confidence = copilotUsed ? 92 : 78;
  return (
    <section className="eib__section eib__evidence" data-testid="eib-evidence">
      <button
        type="button"
        className="eib__evidence-head"
        onClick={() => setOpen((v) => !v)}
        data-testid="eib-evidence-toggle"
        aria-expanded={open}
      >
        <div>
          <span className="eib__evidence-eyebrow">
            <Sparkles className="w-3.5 h-3.5" /> AI Evidence & Confidence
          </span>
          <span className="eib__evidence-sub">
            Confidence {confidence}% · {sources.length} sources · {recommendationCount} recommendations
          </span>
        </div>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {open && (
        <div className="eib__evidence-body" data-testid="eib-evidence-body">
          <div className="eib__evidence-columns">
            <div>
              <h5>Data sources analyzed</h5>
              <ul>
                {sources.map((s, i) => (
                  <li key={i}><span className="eib__evidence-check">✓</span> {s}</li>
                ))}
              </ul>
            </div>
            <div>
              <h5>Freshness &amp; confidence</h5>
              <dl className="eib__evidence-dl">
                <dt>AI generator</dt><dd>{copilotUsed ? "HarborWell Copilot (Claude Sonnet)" : "Deterministic fallback"}</dd>
                <dt>Confidence score</dt><dd>{confidence}%</dd>
                <dt>Datasets analyzed</dt><dd>{sources.length}</dd>
                <dt>Last refresh</dt><dd>{digestPostedAt ? new Date(digestPostedAt).toLocaleString() : "—"}</dd>
                <dt>Read-only</dt><dd>Yes — this panel does not modify data</dd>
              </dl>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Feature 7 — Executive Timeline
// ─────────────────────────────────────────────────────────────────────────────
const TIMELINE_BUCKETS = [
  { key: "today", label: "Today", days: 1 },
  { key: "yesterday", label: "Yesterday", days: 2 },
  { key: "this_week", label: "This week", days: 7 },
  { key: "last_30", label: "Last 30 days", days: 30 },
];

function bucketizeEvents(events) {
  const buckets = { today: [], yesterday: [], this_week: [], last_30: [] };
  const now = Date.now();
  const oneDay = 24 * 60 * 60 * 1000;
  events.forEach((ev) => {
    const ts = new Date(ev.at || ev.timestamp || ev.created_at || 0).getTime();
    if (!ts) return;
    const age = now - ts;
    if (age < oneDay) buckets.today.push(ev);
    else if (age < 2 * oneDay) buckets.yesterday.push(ev);
    else if (age < 7 * oneDay) buckets.this_week.push(ev);
    else if (age < 30 * oneDay) buckets.last_30.push(ev);
  });
  return buckets;
}

function ExecutiveTimeline() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get("/executive-ops/timeline?limit=60&since_days=30")
      .then(({ data }) => { if (!cancelled) setEvents(data?.events || []); })
      .catch(() => { if (!cancelled) setEvents([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const buckets = useMemo(() => bucketizeEvents(events), [events]);
  const totalBucketed = buckets.today.length + buckets.yesterday.length +
    buckets.this_week.length + buckets.last_30.length;

  if (loading) return null;
  if (!totalBucketed) {
    return (
      <section className="eib__section" data-testid="eib-timeline">
        <div className="eib__section-head">
          <h3><Clock className="w-4 h-4" /> Executive Timeline</h3>
        </div>
        <div className="eib__empty">
          <Circle className="w-5 h-5 text-slate-400" />
          <div>
            <strong>No executive events in the last 30 days.</strong>
            <p>Timeline events include compliance milestones, security incidents, and operational alerts.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="eib__section" data-testid="eib-timeline">
      <div className="eib__section-head">
        <h3><Clock className="w-4 h-4" /> Executive Timeline</h3>
        <span className="eib__section-sub">Recent organizational activity — click any event to deep-link</span>
      </div>
      <div className="eib__timeline">
        {TIMELINE_BUCKETS.map(({ key, label }) => {
          const list = buckets[key];
          if (!list.length) return null;
          return (
            <div key={key} className="eib__timeline-bucket"
                 data-testid={`eib-timeline-${key}`}>
              <h4 className="eib__timeline-bucket-label">{label}</h4>
              <ul className="eib__timeline-list">
                {list.slice(0, 6).map((ev, i) => (
                  <li key={ev.id || i} className="eib__timeline-item">
                    <span className={`eib__timeline-dot eib__timeline-dot--${(ev.severity || "info").toLowerCase()}`} />
                    <div className="eib__timeline-content">
                      <div className="eib__timeline-title">
                        {ev.link ? (
                          <Link to={ev.link}>{ev.title || ev.summary || ev.action}</Link>
                        ) : (ev.title || ev.summary || ev.action)}
                      </div>
                      {ev.detail && <div className="eib__timeline-detail">{ev.detail}</div>}
                      <div className="eib__timeline-meta">
                        {new Date(ev.at || ev.timestamp || ev.created_at).toLocaleString()}
                        {ev.source && ` · ${ev.source}`}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Feature 9 — Board-ready reporting (Export & Print)
// ─────────────────────────────────────────────────────────────────────────────
function BoardReportingToolbar({ onPrint, onExportSummary, onExportFull, digestPostedAt }) {
  return (
    <div className="eib__toolbar" data-testid="eib-toolbar">
      <div className="eib__toolbar-status">
        <Sunrise className="w-4 h-4" />
        <span>Executive Intelligence Briefing</span>
        {digestPostedAt && (
          <span className="eib__toolbar-date">
            · {new Date(digestPostedAt).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </span>
        )}
      </div>
      <div className="eib__toolbar-actions">
        <button type="button" onClick={onPrint}
                className="eib__btn eib__btn--outline"
                data-testid="eib-print">
          <Printer className="w-4 h-4" /> Print
        </button>
        <button type="button" onClick={onExportSummary}
                className="eib__btn eib__btn--outline"
                data-testid="eib-export-summary">
          <Download className="w-4 h-4" /> Executive summary
        </button>
        <button type="button" onClick={onExportFull}
                className="eib__btn eib__btn--primary"
                data-testid="eib-export-full">
          <FileText className="w-4 h-4" /> Full report
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Container
// ─────────────────────────────────────────────────────────────────────────────
export default function ExecutiveIntelligenceBriefing({ overview }) {
  const [digest, setDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/executive-ops/daily-digest/latest")
      .then(({ data }) => { if (!cancelled) setDigest(data); })
      .catch(() => { if (!cancelled) setDigest(null); })
      .finally(() => { if (!cancelled) setDigestLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const parsed = useMemo(() => parseNarrative(digest?.narrative), [digest?.narrative]);
  const recommendations = useMemo(
    () => buildRecommendations(overview, parsed.recommendations, digest?.citations),
    [overview, parsed.recommendations, digest?.citations]
  );

  const doPrint = () => {
    document.body.classList.add("eib-print-mode");
    setTimeout(() => {
      window.print();
      document.body.classList.remove("eib-print-mode");
    }, 100);
  };

  const doExport = (kind) => {
    const payload = {
      kind,
      exported_at: new Date().toISOString(),
      digest_posted_at: digest?.posted_at,
      copilot_used: digest?.copilot_used,
      executive_summary: overview?.executive_summary,
      summary_bullets: parsed.summaryBullets,
      risks: buildRiskItems(overview),
      recommendations,
      citations: digest?.citations || [],
      framework_readiness: overview?.framework_readiness,
      timeline_note: "Timeline available in dashboard view.",
    };
    if (kind === "summary") {
      delete payload.framework_readiness;
      delete payload.risks;
    }
    const blob = new Blob([JSON.stringify(payload, null, 2)],
      { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `harborwell-executive-briefing-${kind}-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="eib" data-testid="executive-intelligence-briefing">
      <BoardReportingToolbar
        onPrint={doPrint}
        onExportSummary={() => doExport("summary")}
        onExportFull={() => doExport("full")}
        digestPostedAt={digest?.posted_at}
      />

      {/* Visual hierarchy (Feature 10): Snapshot → Summary → Risks → Actions → Evidence → Timeline */}
      <ExecutiveSnapshot overview={overview} digestPostedAt={digest?.posted_at} />

      {!digestLoading && parsed.summaryBullets.length > 0 && (
        <ExecutiveSummary bullets={parsed.summaryBullets}
                          hasCopilot={!!digest?.copilot_used} />
      )}

      <CriticalRiskCenter overview={overview} />

      <RecommendedActions recommendations={recommendations}
                          citations={digest?.citations}
                          digestPostedAt={digest?.posted_at} />

      <EvidencePanel citations={digest?.citations}
                     copilotUsed={digest?.copilot_used}
                     digestPostedAt={digest?.posted_at}
                     recommendationCount={recommendations.length} />

      <ExecutiveTimeline />
    </div>
  );
}
